import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { FileText, DollarSign, Search, Eye } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const AccountsPayable = ({ user }) => {
  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [formData, setFormData] = useState({
    proveedor_id: '',
    numero_factura: '',
    ncf: '',
    descripcion: '',
    monto_total: '',
    fecha_factura: '',
    fecha_vencimiento: '',
    notas: '',
  });
  const [paymentData, setPaymentData] = useState({
    monto: '',
    tipo_pago: 'efectivo',
    referencia: '',
    notas: '',
  });

  useEffect(() => {
    fetchAccounts();
    fetchSuppliers();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts-payable`);
      setAccounts(response.data);
    } catch (error) {
      toast.error('Error al cargar cuentas');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error loading suppliers');
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/accounts-payable`, {
        ...formData,
        monto_total: parseFloat(formData.monto_total),
        fecha_factura: new Date(formData.fecha_factura).toISOString(),
        fecha_vencimiento: new Date(formData.fecha_vencimiento).toISOString(),
      });
      toast.success('Cuenta por pagar creada');
      setDialogOpen(false);
      resetForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cuenta');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/accounts-payable/${selectedAccount.id}/payments`, {
        ...paymentData,
        monto: parseFloat(paymentData.monto),
      });
      toast.success('Pago registrado exitosamente');
      setPaymentDialogOpen(false);
      setSelectedAccount(null);
      resetPaymentForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    }
  };

  const resetForm = () => {
    setFormData({
      proveedor_id: '',
      numero_factura: '',
      ncf: '',
      descripcion: '',
      monto_total: '',
      fecha_factura: '',
      fecha_vencimiento: '',
      notas: '',
    });
  };

  const resetPaymentForm = () => {
    setPaymentData({
      monto: '',
      tipo_pago: 'efectivo',
      referencia: '',
      notas: '',
    });
  };

  const openPaymentDialog = (account) => {
    setSelectedAccount(account);
    setPaymentData({ ...paymentData, monto: account.monto_pendiente.toString() });
    setPaymentDialogOpen(true);
  };

  const filteredAccounts = accounts.filter(
    (acc) =>
      acc.proveedor_nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.numero_factura.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'pagada':
        return 'bg-green-100 text-green-700';
      case 'pendiente':
        return 'bg-amber-100 text-amber-700';
      case 'vencida':
        return 'bg-red-100 text-red-700';
      case 'parcial':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pagada': return 'Pagada';
      case 'pendiente': return 'Pendiente';
      case 'vencida': return 'Vencida';
      case 'parcial': return 'Parcial';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounts-payable-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Cuentas por Pagar</h1>
          <p className="text-slate-600 mt-1">Gestiona las deudas con proveedores</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="create-account-btn">
              <FileText className="mr-2" size={18} />
              Nueva Cuenta por Pagar
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="create-account-dialog">
            <DialogHeader>
              <DialogTitle>Crear Cuenta por Pagar</DialogTitle>
              <DialogDescription>Registre una nueva factura de proveedor</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateAccount} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="proveedor_id">Proveedor *</Label>
                  <Select value={formData.proveedor_id} onValueChange={(value) => setFormData({ ...formData, proveedor_id: value })} required>
                    <SelectTrigger data-testid="form-proveedor-select">
                      <SelectValue placeholder="Seleccione un proveedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((sup) => (
                        <SelectItem key={sup.id} value={sup.id}>
                          {sup.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="numero_factura">Número Factura *</Label>
                  <Input
                    id="numero_factura"
                    value={formData.numero_factura}
                    onChange={(e) => setFormData({ ...formData, numero_factura: e.target.value })}
                    required
                    data-testid="form-numero-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ncf">NCF</Label>
                  <Input
                    id="ncf"
                    value={formData.ncf}
                    onChange={(e) => setFormData({ ...formData, ncf: e.target.value })}
                    placeholder="Opcional"
                    data-testid="form-ncf-input"
                  />
                </div>
                <div>
                  <Label htmlFor="monto_total">Monto Total *</Label>
                  <Input
                    id="monto_total"
                    type="number"
                    step="0.01"
                    value={formData.monto_total}
                    onChange={(e) => setFormData({ ...formData, monto_total: e.target.value })}
                    required
                    data-testid="form-monto-input"
                  />
                </div>
                <div>
                  <Label htmlFor="fecha_factura">Fecha Factura *</Label>
                  <Input
                    id="fecha_factura"
                    type="date"
                    value={formData.fecha_factura}
                    onChange={(e) => setFormData({ ...formData, fecha_factura: e.target.value })}
                    required
                    data-testid="form-fecha-factura-input"
                  />
                </div>
                <div>
                  <Label htmlFor="fecha_vencimiento">Fecha Vencimiento *</Label>
                  <Input
                    id="fecha_vencimiento"
                    type="date"
                    value={formData.fecha_vencimiento}
                    onChange={(e) => setFormData({ ...formData, fecha_vencimiento: e.target.value })}
                    required
                    data-testid="form-fecha-venc-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="descripcion">Descripción *</Label>
                  <Textarea
                    id="descripcion"
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    required
                    rows={2}
                    data-testid="form-descripcion-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="notas">Notas</Label>
                  <Textarea
                    id="notas"
                    value={formData.notas}
                    onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                    rows={2}
                    data-testid="form-notas-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="submit-account-btn">
                Crear Cuenta por Pagar
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
        <Input
          placeholder="Buscar cuentas..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-accounts-input"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="custom-table">
            <thead>
              <tr>
                <th>Proveedor</th>
                <th>Factura</th>
                <th>NCF</th>
                <th>Descripción</th>
                <th>Total</th>
                <th>Pendiente</th>
                <th>Vencimiento</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody data-testid="accounts-table-body">
              {filteredAccounts.map((account) => (
                <tr key={account.id} data-testid={`account-row-${account.id}`}>
                  <td className="font-medium">{account.proveedor_nombre}</td>
                  <td>{account.numero_factura}</td>
                  <td className="text-sm text-slate-600">{account.ncf || 'N/A'}</td>
                  <td className="text-sm text-slate-600">{account.descripcion}</td>
                  <td className="font-semibold">{formatCurrency(account.monto_total)}</td>
                  <td className="font-semibold text-red-600">{formatCurrency(account.monto_pendiente)}</td>
                  <td className="text-sm">{new Date(account.fecha_vencimiento).toLocaleDateString('es-DO')}</td>
                  <td>
                    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(account.status)}`}>
                      {getStatusLabel(account.status)}
                    </span>
                  </td>
                  <td>
                    {account.status !== 'pagada' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPaymentDialog(account)}
                        data-testid={`pay-btn-${account.id}`}
                      >
                        <DollarSign size={16} className="mr-1" />
                        Pagar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredAccounts.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No se encontraron cuentas por pagar
          </div>
        )}
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent data-testid="payment-dialog">
          {selectedAccount && (
            <>
              <DialogHeader>
                <DialogTitle>Registrar Pago</DialogTitle>
                <DialogDescription>
                  {selectedAccount.proveedor_nombre} - Factura {selectedAccount.numero_factura}
                </DialogDescription>
              </DialogHeader>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span>Total:</span>
                    <span className="font-semibold">{formatCurrency(selectedAccount.monto_total)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Pendiente:</span>
                    <span className="font-semibold text-red-600">{formatCurrency(selectedAccount.monto_pendiente)}</span>
                  </div>
                </div>
              </div>
              <form onSubmit={handlePayment} className="space-y-4">
                <div>
                  <Label htmlFor="monto">Monto a Pagar *</Label>
                  <Input
                    id="monto"
                    type="number"
                    step="0.01"
                    value={paymentData.monto}
                    onChange={(e) => setPaymentData({ ...paymentData, monto: e.target.value })}
                    required
                    max={selectedAccount.monto_pendiente}
                    data-testid="payment-monto-input"
                  />
                </div>
                <div>
                  <Label htmlFor="tipo_pago">Forma de Pago *</Label>
                  <Select value={paymentData.tipo_pago} onValueChange={(value) => setPaymentData({ ...paymentData, tipo_pago: value })}>
                    <SelectTrigger data-testid="payment-tipo-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="tarjeta">Tarjeta</SelectItem>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="referencia">Referencia</Label>
                  <Input
                    id="referencia"
                    value={paymentData.referencia}
                    onChange={(e) => setPaymentData({ ...paymentData, referencia: e.target.value })}
                    placeholder="Número de referencia o cheque"
                    data-testid="payment-ref-input"
                  />
                </div>
                <div>
                  <Label htmlFor="notas_pago">Notas</Label>
                  <Textarea
                    id="notas_pago"
                    value={paymentData.notas}
                    onChange={(e) => setPaymentData({ ...paymentData, notas: e.target.value })}
                    rows={2}
                    data-testid="payment-notas-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="submit-payment-btn">
                  Registrar Pago
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsPayable;
